import { Edge, Node } from 'reactflow';

import { ChainEditorWidget } from '@/components/chain-editor-widget';
import { Page } from '@/components/page';
import { LogWidget } from '@/components/log-widget/log-widget';
import { useParams } from 'react-router-dom';
import { useLog } from '@/hooks/use-log';
import { useChainLog } from '@/hooks/use-chain-log';
import { Widget } from '@/components/widget';
import { CheckCircleIcon, EllipsisHorizontalCircleIcon, PauseCircleIcon, ExclamationCircleIcon, ArrowRightCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { TimeFromUnix } from '@/components/time-from-unix';
import { Editor } from '@monaco-editor/react';
import clsx from 'clsx';
import { useState } from 'react';
import { PlayCircleIcon } from '@heroicons/react/20/solid';
import { useRerunGraphAction } from '@/hooks/use-rerun-graph-action';
import { AuthorisedFunction } from '@/utilities/authorised-function';

const WIDGET_ID = 'CONTEXT_CHAIN_WIDGET';

const monacoEditorOptions = {
  minimap: {
    enabled: false,
  },
  automaticLayout: true,
  readOnly: true
};

export function ChainLog() {

  const { contextUuid } = useParams();
  const { data } = useChainLog(contextUuid);
  const [iconHover, setIconHover] = useState<string|null>(null);
  const [iconLoading, setIconLoading] = useState<string|null>(null);
  const rerunGraphAction = useRerunGraphAction();

  const handleRepeatStep = (uuid: string) => {
    // console.log(uuid);
    setIconLoading(uuid);
    rerunGraphAction.mutateAsync({ uuid }).then(() => {
      setIconLoading(null);
    });
  }

  return (
    <Page title="Chain Log">
      <Widget id={WIDGET_ID} title="Running Chain">
      <div className="flow-root">
        <p className="mb-4 text-sm text-gray-400">Chain Context UUID: { contextUuid }</p>
        <ul role="list" className="-mb-8">
          {
            data?.data?.map((item, i) => (
              <li key={item.uuid}>
                <div className="relative pb-8">
              {i !== data.data.length - 1 ? (
                <span className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
              ) : null}
              <div className="relative flex space-x-3">
                <div>
                  <span
                    className={clsx(
                      'h-8 w-8 rounded-full flex items-center justify-center'
                    )}
                    onMouseEnter={() => setIconHover(item.uuid)}
                    onMouseLeave={() => setIconHover(null)}
                  >
                    {iconHover === item.uuid && item.bundleFunction && AuthorisedFunction(['developer']) && iconLoading !== item.uuid ? (
                      <PlayCircleIcon className="h-8 w-8 text-indigo-500 bg-white hover:cursor-pointer" aria-hidden="true" title="Rerun step" onClick={() => handleRepeatStep(item.uuid)} />
                    ) : iconLoading === item.uuid ? ( 
                      <div className="relative inline-flex items-center justify-center w-6 h-6 overflow-hidden bg-indigo-500 rounded-full">
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </div>
                    ) : (
                      <>
                        {item.status === "completed" || item.status === "complete" &&
                          <CheckCircleIcon className="h-8 w-8 text-indigo-500 bg-white" aria-hidden="true" />   
                        }
                        {item.status === "created" &&
                          <InformationCircleIcon className="h-8 w-8 text-indigo-500 bg-white" aria-hidden="true" />   
                        }
                        {item.status === "running" &&
                          <EllipsisHorizontalCircleIcon className="h-8 w-8 text-indigo-500 bg-white" aria-hidden="true" />   
                        }
                        {item.status === "failed" &&
                          <ExclamationCircleIcon className="h-8 w-8 text-indigo-500 bg-white" aria-hidden="true" />   
                        }
                        {item.status === "delayed" &&
                          <PauseCircleIcon className="h-8 w-8 text-indigo-500 bg-white" aria-hidden="true" />
                        }
                        {item.status === "skipped" &&
                          <ArrowRightCircleIcon className="h-8 w-8 text-indigo-500 bg-white" aria-hidden="true" />
                        }
                      </>
                    )}
                  </span>
                </div>
                <div className="flex flex-wrap min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                  <div>
                    <p className="text-sm text-gray-500">
                      {item.title}{' with status '}
                      <span className="font-medium text-gray-900">
                        {item.status}
                      </span>
                    </p>
                  </div>
                  <div className="whitespace-nowrap text-right text-sm text-gray-500">
                    <TimeFromUnix unixTimestamp={item.timestamp} />
                  </div>
                  {item.log &&
                    item.log.map((log, x) => (
                      <div key={x} className="w-full mt-3 flex-auto rounded-md p-3 ring-1 ring-inset ring-gray-200">
                        <div className="flex justify-between gap-x-4">
                          <div className="py-0.5 text-xs leading-5 text-gray-500">Logged with level <span className="font-medium text-gray-900">{log.level}</span></div>
                          <div className="flex-none py-0.5 text-xs leading-5 text-gray-500"><TimeFromUnix unixTimestamp={log.time} /></div>
                        </div>
                        {typeof log.message === 'string' && (
                          <p className="text-sm leading-6 text-gray-500">{log.message}</p>
                        )}
                        {typeof log.message === 'object' && (
                          <p className="text-sm leading-6 text-gray-500">This log message can not be displayed as it contains an object.</p>
                        )}
                        {log.json && typeof log.json === 'string' && (
                          <Editor
                            value={log.json}
                            language="json"
                            theme="vs-light"
                            height={300}
                            options={monacoEditorOptions}
                          />
                        )}
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
              </li>
            ))
          }
        </ul>
      </div>
    </Widget>
    </Page>
  );
}
